import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import {
  submitReport,
  REPORT_CATEGORIES,
  REPORT_CATEGORY_LABELS,
  type ReportCategory,
  type ReportTargetType,
} from "../../api/reader-report";
import { ApiError } from "../../api/client";
import { useDialogKeyboard } from "../ui/useDialogKeyboard";

/**
 * moderation-pipeline-reader-report (Phase 5+ Tier 5A 第 2 段) Phase 4:
 * reader 通報 modal。 sign-in 済 reader が scene / scenario / creator を通報する単一 modal。
 *
 * 配置: standalone reusable component (= 任意の reader UI surface から trigger 可能)、
 * sibling: SummaryEditModal と同 placement-agnostic doctrine。
 *
 * UX 設計:
 *   - category radio (7 種)
 *   - 任意 comment textarea (max 500 文字)
 *   - 429 (= rate limit) / 401 (= sign-in 必須) / 403 で適切な error message
 *   - submit success 後 「受け付けました」 message → 3 秒後 close
 *   - 初期 focus は category radio 群の先頭、 Escape で close
 */
export interface ReportModalProps {
  baseUrl: string;
  targetType: ReportTargetType;
  targetId: string;
  targetLabel: string;
  onClose: () => void;
}

const MAX_COMMENT_LENGTH = 500;

export function ReportModal({
  baseUrl,
  targetType,
  targetId,
  targetLabel,
  onClose,
}: ReportModalProps): JSX.Element {
  const [category, setCategory] = useState<ReportCategory | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // useDialogKeyboard (= Escape close + initial focus + Tab focus trap) を使う。
  // submit 中は close 抑制したいため onClose を wrap して isSubmitting guard を維持。
  const handleEscapeClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);
  useDialogKeyboard({ containerRef: dialogRef, onClose: handleEscapeClose });

  // submit 成功後 3 秒で自動 close
  useEffect(() => {
    if (!isSubmitted) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [isSubmitted, onClose]);

  async function handleSubmit(): Promise<void> {
    if (category === null || isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await submitReport(baseUrl, {
        targetType,
        targetId,
        category,
        ...(comment.length > 0 ? { comment } : {}),
      });
      setIsSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          err.status === 401
            ? "通報にはサインインが必要です"
            : err.status === 403
              ? "通報する権限がありません"
              : err.status === 429
                ? "同じ対象は 24 時間に 1 回のみ通報できます"
                : `通報に失敗しました (status=${err.status})`,
        );
      } else {
        setError("通信エラーが発生しました");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
      data-testid="report-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-reader-overlay"
      ref={dialogRef}
    >
      <div className="w-full max-w-md rounded-lg bg-reader-page text-reader-primary p-6 shadow-xl">
        <h2
          id="report-modal-title"
          className="text-lg font-bold mb-1"
        >
          通報する
        </h2>
        <p className="text-sm text-reader-secondary mb-4">対象: {targetLabel}</p>

        {isSubmitted ? (
          <div
            role="status"
            data-testid="report-submitted"
            className="rounded bg-reader-success-bg p-3 text-sm text-reader-success-fg"
          >
            通報を受け付けました。 ご協力ありがとうございます。
          </div>
        ) : (
          <>
            <fieldset className="mb-4">
              <legend className="text-sm font-medium mb-2">通報カテゴリ</legend>
              <div className="space-y-1">
                {REPORT_CATEGORIES.map((cat) => (
                  <label
                    key={cat}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <input
                      type="radio"
                      name="report-category"
                      value={cat}
                      checked={category === cat}
                      onChange={() => setCategory(cat)}
                      disabled={isSubmitting}
                      data-testid={`report-category-${cat}`}
                    />
                    {REPORT_CATEGORY_LABELS[cat]}
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="block mb-4">
              <span className="text-sm font-medium">補足 (任意)</span>
              <textarea
                data-testid="report-comment-textarea"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={MAX_COMMENT_LENGTH}
                rows={3}
                disabled={isSubmitting}
                className="mt-1 w-full rounded border border-reader-border p-2 text-sm bg-reader-page text-reader-primary"
                placeholder="詳細を記入できます"
              />
              <span className="text-xs text-reader-muted">
                {comment.length} / {MAX_COMMENT_LENGTH} 文字
              </span>
            </label>

            {error !== null && (
              <div
                role="alert"
                data-testid="report-error"
                className="mb-3 rounded bg-reader-danger-bg p-2 text-sm text-reader-danger-fg"
              >
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="report-cancel"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded border border-reader-border-strong text-reader-secondary px-4 py-2 text-sm"
              >
                キャンセル
              </button>
              <button
                type="button"
                data-testid="report-submit"
                onClick={handleSubmit}
                disabled={category === null || isSubmitting}
                className="rounded bg-reader-brand hover:bg-reader-brand-hover px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                {isSubmitting ? "送信中..." : "送信"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
